import { api, buildQueryParams, URLs } from '@/libs/api'
import type {
  ApiResponse,
  UpdateProfilePicPayload,
  UploadListData,
  UploadListParams,
  UploadPayload,
} from '@/libs/interfaces'
import { uploadsApiMock } from '@/libs/mocks/handlers/uploads'

const uploadsApiReal = {
  // The backend always inserts new uploads with profilePic=false (it only
  // uses this request's `profilePic` flag to unset any *previous* profile
  // pic for the same entity) — the returned uploadID must be passed to
  // updateProfilePic afterwards to actually flag the new upload as current.
  upload: async (payload: UploadPayload): Promise<{ uploadID: string }> => {
    const { orgID, file, entity, entityID, fileType, fileName, profilePic } = payload
    const formData = new FormData()
    formData.append('file', file)
    formData.append('entity', entity)
    formData.append('entityID', entityID)
    formData.append('fileType', fileType)
    formData.append('fileName', fileName)
    formData.append('profilePic', String(profilePic))
    return await api
      .post<ApiResponse<{ uploadID: string }>>(URLs.Uploads.create(orgID), formData)
      .then((response) => response.data.data)
  },

  list: async (params: UploadListParams): Promise<UploadListData> => {
    const { orgID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    return await api
      .get<ApiResponse<UploadListData>>(URLs.Uploads.list(orgID, query))
      .then((response) => response.data.data)
  },

  getPreSignedUrl: async (params: { orgID: string; uploadID: string }): Promise<string> => {
    return await api
      .get<ApiResponse<{ preSignedUrl: string }>>(URLs.Uploads.byID(params.orgID, params.uploadID))
      .then((response) => response.data.data.preSignedUrl)
  },

  delete: async (params: { orgID: string; uploadID: string }): Promise<{ uploadID: string }> => {
    return await api
      .delete<ApiResponse<{ uploadID: string }>>(URLs.Uploads.byID(params.orgID, params.uploadID))
      .then((response) => response.data.data)
  },

  updateProfilePic: async (
    payload: UpdateProfilePicPayload,
  ): Promise<{ uploadID: string; profilePic: boolean }> => {
    const { orgID, uploadID, ...body } = payload
    return await api
      .patch<ApiResponse<{ uploadID: string; profilePic: boolean }>>(
        URLs.Uploads.profilePic(orgID, uploadID),
        body,
      )
      .then((response) => response.data.data)
  },
}

export const uploadsApi: typeof uploadsApiReal = __IS_MOCK_MODE__ ? uploadsApiMock : uploadsApiReal
